// src/components/shopper/ShopperConfirmation.js
import React, { useEffect, useState } from 'react';
import { useLocation }                from 'react-router-dom';
import { jsPDF }                      from 'jspdf';
import {
  Container, Box, Paper, Typography, Button,
  TextField, List, ListItem, ListItemText,
  Divider, CircularProgress
} from '@mui/material';

export default function ShopperConfirmation() {
  const { search } = useLocation();
  const params     = new URLSearchParams(search);
  const intentId   = params.get('payment_intent');

  const [order, setOrder]         = useState(null);
  const [loading, setLoading]     = useState(false);
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupOrderId, setLookupOrderId] = useState('');
  const [lookupResult, setLookupResult]   = useState(null);

  useEffect(() => {
    if (!intentId) return;
    setLoading(true);
    fetch(`/orders/${intentId}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(setOrder)
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [intentId]);

  const downloadReceipt = o => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Order Receipt', 20, 20);
    doc.setFontSize(12);
    doc.text(`Order ID: ${o.id}`,    20, 30);
    doc.text(`Date: ${new Date(o.createdAt).toLocaleString()}`, 20, 40);

    o.items.forEach((i,idx)=> {
      doc.text(
        `${i.name} x${i.qty} — €${i.subtotal.toFixed(2)}`,
        20, 50 + idx*8
      );
    });
    const y = 50 + o.items.length*8 + 10;
    doc.text(`Shipping: €${o.shippingCost.toFixed(2)}`, 20, y);
    doc.text(`Total: €${o.total.toFixed(2)}`,           20, y+10);
    doc.save(`receipt_${o.id}.pdf`);
  };

  const handleLookup = () => {
    fetch(`/orders/lookup?email=${encodeURIComponent(lookupEmail)}&orderId=${lookupOrderId}`)
      .then(r=>r.ok? r.json():Promise.reject())
      .then(setLookupResult)
      .catch(_=>setLookupResult({error:'Not found'}));
  };

  return (
    <Container sx={{py:4}}>
      <Typography variant="h4" gutterBottom>Order Confirmation</Typography>

      {loading ? (
        <Box sx={{textAlign:'center',py:4}}>
          <CircularProgress/><Typography sx={{mt:2}}>Loading…</Typography>
        </Box>
      ) : order ? (
        <Paper sx={{p:3,mb:4}}>
          <Typography variant="h6">Thank you for your purchase!</Typography>
          <Typography>Order ID: {order.id}</Typography>
          <Typography>Total Paid: €{order.total.toFixed(2)}</Typography>
          <Button variant="contained" sx={{mt:2}} onClick={()=>downloadReceipt(order)}>
            Download Receipt
          </Button>
        </Paper>
      ) : (
        <Typography color="error">Unable to load order details.</Typography>
      )}

      <Paper sx={{p:3}}>
        <Typography variant="h6">Manage Your Order</Typography>
        <Box sx={{display:'flex',gap:2,mt:2,flexWrap:'wrap',alignItems:'center'}}>
          <TextField label="Email"   value={lookupEmail}   onChange={e=>setLookupEmail(e.target.value)}/>
          <TextField label="Order ID" value={lookupOrderId} onChange={e=>setLookupOrderId(e.target.value)}/>
          <Button variant="contained" onClick={handleLookup}>Lookup</Button>
        </Box>

        {lookupResult && (
          <Box sx={{mt:2}}>
            {lookupResult.error ? (
              <Typography color="error">{lookupResult.error}</Typography>
            ) : (
              <>
                <Typography>Order ID: {lookupResult.id}</Typography>
                <Typography>
                  Status: {lookupResult.status || 'Received'}
                </Typography>
                <List>
                  {lookupResult.items.map((i,idx)=>(
                    <ListItem key={idx}>
                      <ListItemText
                        primary={`${i.name} x${i.qty}`}
                        secondary={`€${i.subtotal.toFixed(2)}`}
                      />
                    </ListItem>
                  ))}
                </List>
                <Typography>Shipping: €{lookupResult.shippingCost.toFixed(2)}</Typography>
                <Typography>Total: €{lookupResult.total.toFixed(2)}</Typography>
              </>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
}
